import type {
  Order,
  OrderLine,
  Return,
  ReturnResolution,
  ReturnStatus,
} from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { money, type Money } from '@nucleus-commerce/core';

export type ReturnMove =
  'approved' | 'rejected' | 'in_transit' | 'received' | 'inspected' | 'cancelled';

// Same table as the backend's returns service; anything else answers 409 with meta.allowed.
const NEXT: Partial<Record<ReturnStatus, readonly ReturnMove[]>> = {
  requested: ['approved', 'rejected', 'cancelled'],
  approved: ['in_transit', 'received', 'cancelled'],
  in_transit: ['received', 'cancelled'],
  received: ['inspected', 'cancelled'],
  inspected: ['cancelled'],
};

export function nextStatuses(status: ReturnStatus): readonly ReturnMove[] {
  return NEXT[status] ?? [];
}

export function isOpenReturn(status: ReturnStatus): boolean {
  return NEXT[status] !== undefined;
}

/** Resolution needs a finished inspection. */
export function canResolve(status: ReturnStatus): boolean {
  return status === 'inspected';
}

/** Units per order line held by returns that still count: open or resolved, not rejected or cancelled. */
export function unitsInReturns(
  returns: ReadonlyArray<Pick<Return, 'status' | 'lines'>>,
): Map<string, number> {
  const taken = new Map<string, number>();
  for (const ret of returns) {
    if (ret.status === 'rejected' || ret.status === 'cancelled') continue;
    for (const line of ret.lines) {
      taken.set(line.orderLineId, (taken.get(line.orderLineId) ?? 0) + line.quantity);
    }
  }
  return taken;
}

/** Shipped units of a line that no other return holds; the backend's cap for a new return. */
export function returnableQuantity(
  line: Pick<OrderLine, 'fulfilledQuantity' | 'cancelledQuantity'>,
  taken: number,
): number {
  return Math.max(0, line.fulfilledQuantity - line.cancelledQuantity - taken);
}

export interface ReturnableLine {
  line: OrderLine;
  max: number;
}

export function returnableLines(order: Pick<Order, 'lines' | 'returns'>): ReturnableLine[] {
  const taken = unitsInReturns(order.returns);
  return order.lines
    .map((line) => ({ line, max: returnableQuantity(line, taken.get(line.id) ?? 0) }))
    .filter((entry) => entry.max > 0);
}

/**
 * What the returned units cost the shopper: unit price less the line discount per unit (floored),
 * the way the backend values them when it defaults the resolve amount.
 */
export function returnedValue(
  ret: Pick<Return, 'lines'>,
  order: Pick<Order, 'lines' | 'currency'>,
): Money {
  const amount = ret.lines.reduce((sum, item) => {
    const line = order.lines.find((row) => row.id === item.orderLineId);
    if (!line || line.quantity === 0) return sum;
    const perUnit = line.unitPrice.amount - Math.floor(line.discount.amount / line.quantity);
    return sum + perUnit * item.quantity;
  }, 0);
  return money(amount, order.currency);
}

function roundHalfEven(value: number): number {
  const floor = Math.floor(value);
  const diff = value - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** The store's suggested fee (`settings.returns.restockingFeePercent`) on the returned value. */
export function suggestedRestockingFee(value: Money, percent: number): Money {
  return money(roundHalfEven((value.amount * percent) / 100), value.currency);
}

/** The backend's default when no amount is sent: returned value minus the fee, never negative. */
export function defaultResolveAmount(value: Money, fee: Money | null): Money {
  return money(Math.max(0, value.amount - (fee?.amount ?? 0)), value.currency);
}

/** Refund and store credit move money; exchange, repair and resize are recorded for follow-up. */
export function isMoneyResolution(resolution: ReturnResolution): boolean {
  return resolution === 'refund' || resolution === 'store_credit';
}

/** Moves the backend said it would allow instead, from a 409's `meta.allowed`. */
export function allowedFromConflict(error: unknown): ReturnMove[] | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null;
  const allowed = error.meta.allowed;
  return Array.isArray(allowed)
    ? allowed.filter((value): value is ReturnMove => typeof value === 'string')
    : null;
}
